import { useEffect, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { METHOD_LOGIN_KEY } from "../../features/auth/config/authRoutes";
import { sendCbidIdentity } from "../../shared/lib/attribution/sendCbidIdentity";
import use123zapStore from "../../stores/123zapStore";

const FEATURE_ACCESS_REVALIDATE_INTERVAL_MS = 5 * 60 * 1000;
const FEATURE_ACCESS_FOCUS_REVALIDATE_MIN_MS = 60 * 1000;

export default function SessionBootstrap({ children }) {
  const { isAuthenticated, user } = useAuth();
  const lastFeatureAccessRevalidationRef = useRef(0);
  const hydrateFeatureAccessFromCache = use123zapStore(
    (state) => state.hydrateFeatureAccessFromCache
  );
  const loadPlanoAtual = use123zapStore((state) => state.loadPlanoAtual);
  const isEmailVerified = String(user?.emailVerified).toLowerCase() === "true";
  const isProfileComplete = String(user?.completeProfile).toLowerCase() === "true";
  const canLoadPrivateContext =
    isAuthenticated && isEmailVerified && isProfileComplete && Boolean(user?.accountId);

  useEffect(() => {
    if (canLoadPrivateContext) {
      const hasCachedAccess = hydrateFeatureAccessFromCache(user.accountId);
      lastFeatureAccessRevalidationRef.current = Date.now();
      loadPlanoAtual(true, user.accountId, { silent: hasCachedAccess });
    }
  }, [
    canLoadPrivateContext,
    hydrateFeatureAccessFromCache,
    loadPlanoAtual,
    user?.accountId,
  ]);

  useEffect(() => {
    if (!canLoadPrivateContext) return undefined;

    const revalidateFeatureAccess = (options = {}) => {
      const now = Date.now();
      const force = Boolean(options.force);

      if (
        !force &&
        now - lastFeatureAccessRevalidationRef.current <
          FEATURE_ACCESS_FOCUS_REVALIDATE_MIN_MS
      ) {
        return;
      }

      lastFeatureAccessRevalidationRef.current = now;
      loadPlanoAtual(true, user.accountId, { silent: true });
    };

    const intervalId = window.setInterval(
      () => revalidateFeatureAccess({ force: true }),
      FEATURE_ACCESS_REVALIDATE_INTERVAL_MS,
    );
    const handleWindowFocus = () => revalidateFeatureAccess();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        revalidateFeatureAccess();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("online", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canLoadPrivateContext, loadPlanoAtual, user?.accountId]);

  /* Vínculo `cbid` ↔ cliente logado (fase 3 do B4, spec 0121 do
   * `briotecnologia/123zap-site-automation`).
   *
   * Este é o ponto certo por uma razão que não é óbvia: a guarda acima já
   * exige `accountId`, e o `accountId` só existe DEPOIS da seleção de conta.
   * Enviar de dentro do `useLoginForm` mandaria conta ambígua para quem tem
   * mais de uma — e a relação pessoa↔conta é muitos-para-muitos. */
  useEffect(() => {
    if (!canLoadPrivateContext) return;

    let methodLogin = null;
    try {
      methodLogin = window.sessionStorage.getItem(METHOD_LOGIN_KEY);
    } catch {
      methodLogin = null;
    }

    sendCbidIdentity({
      accountId: user?.accountId,
      email: user?.email,
      methodLogin,
    });
  }, [canLoadPrivateContext, user?.accountId, user?.email]);

  return children;
}
